import type { FC, ReactNode } from 'react';

import type { MetricAccent } from '@/common-components/MetricCard';

/**
 * The label / value / bar row used by "Campus Activity" on the dashboard and "Top Rented
 * Categories" on Analytics. Ports the prototype's 12.5px semibold label, 6px bar (8px on the
 * analytics variant) and 3px radius.
 */
interface ProgressBarRowProps {
  label: ReactNode;
  /** Pre-formatted display value, e.g. "92%". */
  value: string;
  /** Bar fill, 0–100. */
  percent: number;
  accent?: MetricAccent;
  /** Analytics uses the thicker 8px bar; the dashboard uses 6px. */
  thick?: boolean;
}

const accentVar: Record<MetricAccent, string> = {
  brand: 'var(--color-brand-500)',
  success: 'var(--color-success)',
  info: 'var(--color-info)',
  warn: 'var(--color-warn)',
  danger: 'var(--color-danger)',
  pink: 'var(--color-pink)',
};

const ProgressBarRow: FC<ProgressBarRowProps> = ({
  label,
  value,
  percent,
  accent = 'brand',
  thick = false,
}) => (
  <div>
    <div className="mb-2 flex items-center justify-between">
      <div className="text-[12.5px] font-semibold text-[var(--color-ink)]">{label}</div>
      <div className="text-[12px] font-bold" style={{ color: accentVar[accent] }}>
        {value}
      </div>
    </div>
    <div
      className="overflow-hidden rounded-[3px] bg-[var(--color-surface-muted-2)]"
      style={{ height: thick ? 8 : 6 }}
    >
      <div
        className="h-full rounded-[3px]"
        // Clamped: a percentage over 100 would overflow the track and under 0 would invert it.
        style={{
          width: `${Math.max(0, Math.min(100, percent))}%`,
          background: accentVar[accent],
        }}
      />
    </div>
  </div>
);

export default ProgressBarRow;
